import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * Journal des écritures dans `fundamentals` (recette du 2026-10-14, lot B0).
 *
 * Chaque opération (import pipeline, upload, URL, saisie admin) dit quel document ou quelle
 * personne a écrit une ligne financière, quand, et ce qui a changé champ par champ. Les
 * « conflits » sont des valeurs extraites d'un comparatif qui contredisent la base : elles
 * n'ont PAS été écrites et attendent une revue humaine.
 */

// SIMULATION : double circuit du lot B10 (rien n'est écrit) ; AUTO : import automatique réel.
export type SourceImport = 'PIPELINE' | 'UPLOAD' | 'URL' | 'SAISIE_ADMIN' | 'SCRIPT' | 'SIMULATION' | 'AUTO';
export type StatutImport = 'ECRIT' | 'COMPLEMENT' | 'INCHANGE' | 'QUARANTAINE' | 'REFUSE' | 'ERREUR';

export interface OperationImport {
    id: number;
    created_at: string | null;
    source: SourceImport;
    statut: StatutImport;
    societe_symbole: string | null;
    document_exercice: number | null;
    document_period_type: string | null;
    fichier: string | null;
    source_url: string | null;
    discovered_report_id: number | null;
    user_id: number | null;
    nb_items: number;
    nb_lignes_ecrites: number;
    nb_conflits: number;
    nb_alertes: number;
    message: string | null;
}

export type ValeurJournal = number | string | null;

export interface LigneImport {
    exercice: number;
    period_type: string;
    role: 'PRINCIPAL' | 'COMPLEMENT' | 'SAISIE';
    action: string;
    ecritures: Record<string, [ValeurJournal, ValeurJournal]>;
    conflits: Record<string, [ValeurJournal, ValeurJournal]>;
    alertes: string[];
}

export interface DetailImport extends OperationImport {
    pdf_sha256: string | null;
    lignes: LigneImport[];
    alertes: string[];
    items_bruts: unknown;
}

export interface FiltresImport {
    symbole: string;
    source: string;
    statut: string;
    aRevoir: boolean;
}

const TAILLE_PAGE = 50;

/** Rapport de rodage de l'import automatique (lot B10, décision D6). */
export interface EcartRodage {
    discovered_report_id: number;
    societe_symbole: string | null;
    simulation_id: number;
    import_id: number;
    identique: boolean;
    statut_simulation: string;
    statut_valide: string;
    force_par_admin: boolean;
    ecarts: { periode: string; champ: string; simulation: ValeurJournal; valide: ValeurJournal }[];
}

export interface StatsDelai {
    nb: number;
    mediane: number | null;
    max: number | null;
}

export interface EtatInterrupteur {
    actif: boolean;
    modifie_le: string | null;
    modifie_par_user_id: number | null;
}

export interface RapportRodage {
    depuis: string;
    interrupteur_actif: boolean;
    interrupteur: EtatInterrupteur;
    premiere_simulation: string | null;
    fin_rodage_au_plus_tot: string | null;
    nb_simulations: number;
    nb_rapports: number;
    nb_eligibles: number;
    motifs_file_humaine: Record<string, number>;
    libelles_motifs: Record<string, string>;
    comparaison: {
        nb_comparables: number;
        nb_identiques: number;
        taux_identiques: number | null;
        nb_en_attente_d_import_admin: number;
        ecarts: EcartRodage[];
    };
    quarantaines: { nb: number; forcees_par_admin: number; en_attente: number };
    delai_publication_import_jours: { admin: StatsDelai; automatique: StatsDelai };
}

export function useRodageImportAuto(jours = 28) {
    const { token } = useAuth();
    const [rapport, setRapport] = useState<RapportRodage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const charger = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/rodage?jours=${jours}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) {
                setError('Le rapport de rodage de l’import automatique n’a pas pu être chargé.');
                return;
            }
            const corps = await r.json();
            setRapport(corps.data ?? null);
        } catch (err) {
            console.error('Rodage import automatique :', err);
            setError('Erreur réseau. Vérifiez la connexion à l’API.');
        } finally {
            setLoading(false);
        }
    }, [token, jours]);

    useEffect(() => {
        charger();
    }, [charger]);

    /** Bascule l'import automatique. Renvoie true si l'API a confirmé le nouvel état. */
    const basculer = useCallback(
        async (actif: boolean): Promise<boolean> => {
            if (!token) return false;
            try {
                const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/import-automatique`, {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ actif }),
                });
                if (!r.ok) return false;
                const corps = await r.json();
                const etat: EtatInterrupteur | undefined = corps.data;
                if (!etat || etat.actif !== actif) return false;
                setRapport((prec) => (prec ? { ...prec, interrupteur_actif: etat.actif, interrupteur: etat } : prec));
                return true;
            } catch (err) {
                console.error('Bascule import automatique :', err);
                return false;
            }
        },
        [token]
    );

    return { rapport, loading, error, recharger: charger, basculer };
}

export function useFundamentalsImportLog() {
    const { token } = useAuth();
    const [operations, setOperations] = useState<OperationImport[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [filtres, setFiltres] = useState<FiltresImport>({ symbole: '', source: '', statut: '', aRevoir: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [detail, setDetail] = useState<DetailImport | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const charger = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ limit: String(TAILLE_PAGE), offset: String(page * TAILLE_PAGE) });
        if (filtres.symbole.trim()) params.set('symbole', filtres.symbole.trim().toUpperCase());
        if (filtres.source) params.set('source', filtres.source);
        if (filtres.statut) params.set('statut', filtres.statut);
        if (filtres.aRevoir) params.set('a_revoir', 'true');
        try {
            const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) {
                setError(
                    r.status === 403
                        ? 'Accès refusé : permission admin_platform requise.'
                        : 'Le journal des imports n’a pas pu être chargé.'
                );
                return;
            }
            const corps = await r.json();
            setOperations(corps.data?.operations ?? []);
            setTotal(corps.meta?.total ?? 0);
        } catch (err) {
            console.error('Journal des imports :', err);
            setError('Erreur réseau. Vérifiez la connexion à l’API.');
        } finally {
            setLoading(false);
        }
    }, [token, page, filtres]);

    useEffect(() => {
        charger();
    }, [charger]);

    const ouvrir = useCallback(
        async (id: number) => {
            if (!token) return;
            setDetailLoading(true);
            try {
                const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) {
                    setError('Le détail de cette opération n’a pas pu être chargé.');
                    return;
                }
                const corps = await r.json();
                setDetail(corps.data ?? null);
            } catch (err) {
                console.error('Détail du journal :', err);
                setError('Erreur réseau. Vérifiez la connexion à l’API.');
            } finally {
                setDetailLoading(false);
            }
        },
        [token]
    );

    const changerFiltres = useCallback((f: Partial<FiltresImport>) => {
        setPage(0);
        setFiltres((prec) => ({ ...prec, ...f }));
    }, []);

    return {
        operations,
        total,
        page,
        setPage,
        taillePage: TAILLE_PAGE,
        filtres,
        changerFiltres,
        loading,
        error,
        recharger: charger,
        detail,
        detailLoading,
        ouvrir,
        fermer: () => setDetail(null),
    };
}


export interface RapportDecouvertCycle {
    id: number;
    title: string | null;
    statut: string;
    source_url: string | null;
    publication_date: string | null;
}

export interface DernierJournalCycle {
    id: number;
    source: string;
    statut: string;
    message: string | null;
    created_at: string | null;
    nb_conflits: number;
    nb_alertes: number;
}

export interface DetailCycleSuivi {
    exercice: number;
    period_type: string;
    statut: string;
    champs_manquants: string[];
    rapports_decouverts: RapportDecouvertCycle[];
    dernier_journal: DernierJournalCycle | null;
}

export interface InvariantSociete {
    code: string;
    severite: string;
    periode: string;
    message: string;
}

export interface LigneSocieteSuivi {
    symbole: string;
    nom_societe: string;
    secteur: string | null;
    annee_introduction: number | null;
    annee_debut_exigible: number;
    nb_annuels_complets: number;
    nb_annuels_exigibles: number;
    cagr_5ans_calculable: boolean;
    annuels: Record<string, DetailCycleSuivi>;
    periodiques: Record<string, DetailCycleSuivi>;
    invariants: InvariantSociete[];
}

export interface TrouRecuperable extends DetailCycleSuivi {
    symbole: string;
    nom_societe: string;
}

export interface LogFonctionFondamentaux {
    id: number;
    function_name: string;
    status: string;
    message: string | null;
    started_at: string | null;
}

export interface SuiviFondamentauxData {
    annee_courante: number;
    fenetre_annuelle: number[];
    periodes_intermediaires: string[];
    interrupteur_actif: boolean;
    synthese: {
        nb_societes: number;
        taux_couverture_annuelle_pct: number;
        total_annuels_complets: number;
        total_annuels_exigibles: number;
        nb_trous_recuperables: number;
        nb_invariants_bloquants: number;
        nb_societes_avec_invariants: number;
        nb_documents_annexes_ignores: number;
    };
    stats_par_exercice: Record<string, {
        complet: number;
        incomplet: number;
        absent_avec_rapport: number;
        absent_sans_publication: number;
        pre_introduction: number;
    }>;
    trous_recuperables: TrouRecuperable[];
    societes: LigneSocieteSuivi[];
    fundamentals_logs: LogFonctionFondamentaux[];
}

export interface ResultatRattrapage {
    corrections_deterministes: { nb_corrections: number; corrections: string[] };
    rapports_traites: { id: number; societe_symbole: string; titre: string; motifs: string[]; importe: boolean }[];
    message: string;
}

export interface ResultatCorrectionCycle {
    corrige: boolean;
    symbole: string;
    exercice: number;
    period_type: string;
    discovered_report_id?: number;
    nb_lignes_ecrites?: number;
    champs_manquants_restants?: string[];
    etat_avant?: { present: boolean; complet: boolean; champs_manquants: string[] };
    etat_apres?: { present: boolean; complet: boolean; champs_manquants: string[] };
    motifs_file_humaine?: string[];
    detail?: string;
    message?: string;
}

export function useSuiviCouvertureFondamentaux() {
    const { token } = useAuth();
    const [suivi, setSuivi] = useState<SuiviFondamentauxData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rattrapageEnCours, setRattrapageEnCours] = useState(false);
    const [cycleEnCorrection, setCycleEnCorrection] = useState<string | null>(null);

    const charger = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/suivi`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) {
                setError('Le tableau de suivi des fondamentaux n’a pas pu être chargé.');
                return;
            }
            const corps = await r.json();
            setSuivi(corps.data ?? null);
        } catch (err) {
            console.error('Suivi couverture fondamentaux :', err);
            setError('Erreur réseau lors du chargement du suivi des fondamentaux.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        charger();
    }, [charger]);

    const lancerRattrapage = useCallback(
        async (maxRapports = 0, forcerEcriture = true): Promise<ResultatRattrapage | null> => {
            if (!token) return null;
            setRattrapageEnCours(true);
            const dateLancement = new Date().toISOString();
            try {
                // 1. Déclenchement de la tâche en tâche de fond (immunisé contre le timeout HTTP 230s)
                const params = new URLSearchParams({
                    max_rapports: String(maxRapports),
                    arriere_plan: 'true',
                    forcer_ecriture: String(forcerEcriture),
                });
                const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/rattrapage?${params}`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!r.ok) {
                    throw new Error(`Erreur HTTP ${r.status} au lancement du rattrapage`);
                }

                // 2. Polling du journal /dernier-audit toutes les 4s (timeout max 240s)
                const maxTentatives = 60;
                for (let i = 0; i < maxTentatives; i++) {
                    await new Promise(res => setTimeout(res, 4000));
                    const auditParams = new URLSearchParams({ depuis: dateLancement });
                    const rAudit = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/dernier-audit?${auditParams}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (rAudit.ok) {
                        const jsonAudit = await rAudit.json();
                        if (jsonAudit.data?.termine && jsonAudit.data?.resultat) {
                            await charger();
                            return jsonAudit.data.resultat as ResultatRattrapage;
                        }
                    }
                }
                // Si le timeout de polling est atteint, on recharge quand même le suivi
                await charger();
                return {
                    corrections_deterministes: { nb_corrections: 0, corrections: [] },
                    rapports_traites: [],
                    message: "Le traitement continue en arrière-plan. La matrice a été actualisée.",
                };
            } catch (err) {
                console.error('Rattrapage fondamentaux :', err);
                return null;
            } finally {
                setRattrapageEnCours(false);
            }
        },
        [token, charger]
    );

    const corrigerCycle = useCallback(
        async (
            symbole: string,
            exercice: number,
            periodType: string,
            discoveredReportId?: number
        ): Promise<ResultatCorrectionCycle | null> => {
            if (!token) return null;
            const cycleKey = `${symbole}-${exercice}-${periodType}`;
            setCycleEnCorrection(cycleKey);
            const dateLancement = new Date().toISOString();

            try {
                // 1. Lancement de la correction en tâche de fond
                const r = await fetch(`${API_URL}/v1/admin/fundamentals/import-log/corriger-cycle?arriere_plan=true`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        symbole,
                        exercice,
                        period_type: periodType,
                        discovered_report_id: discoveredReportId ?? null,
                    }),
                });

                if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    throw new Error(err.detail || `Erreur ${r.status}`);
                }

                const resInit = await r.json();
                const repId = resInit.data?.discovered_report_id || discoveredReportId;
                if (!repId) {
                    await charger();
                    return {
                        corrige: false,
                        symbole,
                        exercice,
                        period_type: periodType,
                        message: "Rapport introuvable pour ce cycle.",
                    };
                }

                // 2. Polling du statut de correction du cycle toutes les 3s (jusqu'à 150s)
                const maxTentatives = 50;
                for (let i = 0; i < maxTentatives; i++) {
                    await new Promise(res => setTimeout(res, 3000));
                    const params = new URLSearchParams({
                        discovered_report_id: String(repId),
                        depuis: dateLancement,
                    });
                    const rStatut = await fetch(
                        `${API_URL}/v1/admin/fundamentals/import-log/statut-correction-cycle?${params}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (rStatut.ok) {
                        const jsonStatut = await rStatut.json();
                        if (jsonStatut.data?.termine && jsonStatut.data?.resultat) {
                            await charger();
                            return jsonStatut.data.resultat as ResultatCorrectionCycle;
                        }
                    }
                }

                await charger();
                return {
                    corrige: false,
                    symbole,
                    exercice,
                    period_type: periodType,
                    discovered_report_id: repId,
                    message: "Le traitement du PDF prend plus de temps que prévu en tâche de fond. Rechargez dans quelques instants.",
                };
            } catch (err: any) {
                console.error(`Erreur correction cycle ${cycleKey}:`, err);
                return {
                    corrige: false,
                    symbole,
                    exercice,
                    period_type: periodType,
                    discovered_report_id: discoveredReportId,
                    message: err?.message || "Erreur réseau lors de la correction du cycle.",
                };
            } finally {
                setCycleEnCorrection(null);
            }
        },
        [token, charger]
    );

    return {
        suivi,
        loading,
        error,
        recharger: charger,
        lancerRattrapage,
        rattrapageEnCours,
        corrigerCycle,
        cycleEnCorrection,
    };
}
